import type { Scene } from '@/types';

/**
 * Capítulo 2 — "La red debajo del agua".
 *
 * Rework completo. Arco: integración en la Villa, tres facciones con sus
 * misiones (cada una con su voz), revelación de que la plaga que azota a
 * los carpinchos no es natural, giro sobre un personaje querido como
 * saboteador forzado, y cliffhanger: desaparición de alguien cercano.
 *
 * Estructura hub: las tres misiones se desbloquean libremente pero todas
 * deben completarse para avanzar al clímax.
 */
export const CAP2_ESCENAS: Scene[] = [
  // ===========================================================================
  //  ACTO I — "La luz que entra por donde no entraba"
  // ===========================================================================
  {
    id: 'cap2_intro',
    capitulo: 2,
    titulo: 'La red debajo del agua',
    background: { type: 'village', hint: 'villa al amanecer con niebla sobre el río' },
    musica: 'village_warm',
    texto: [
      'Te despertás sin saber cuánto dormiste. Eso es un síntoma: los que duermen poco siempre saben cuánto; los que duermen mal también. Dormir sin enterarse es un lujo que te venía debiendo el cuerpo hace años, y el cuerpo —desgraciado, con su timing impecable— decidió cobrarse ahora, con la villa encima tuyo y la guerra empezada.',
      'Afuera es temprano. La niebla del río llega hasta la mitad de las pasarelas y le da a la aldea un aspecto de sueño de otro. Un carpincho viejo, al que no conocés, barre ceniza del fogón central con una escoba de junco. La barre con paciencia de ritual. Cada vez que termina una zona vuelve a empezar, como si en el acto de barrer estuviera rezándole a algo.',
      '[if flag=pipo_rescatado]Pipo duerme en el hueco que te dejaron a los pies de la hamaca. No está. Lo buscás con la mirada, despacio, sin alarma: la niebla se come las distancias y se acostumbra una a perderlo y encontrarlo. Lo ves, al fin, arrodillado (si esa palabra le cabe a un carpincho) frente a la fuente chica donde ellos acomodan piedras ceremoniales. Tiene la cabeza baja, como si leyera.[/if][if noflag=pipo_rescatado]No hay Pipo. La hamaca de al lado es ancha y vacía, te la dieron por si traías al pibe y vuelve cada vez que te das vuelta como el recordatorio que te dan las cosas que te cuestan. Aprender a dormir al lado de ese vacío va a llevar tiempo.[/if]',
    ],
    onEnter: [
      { kind: 'stat', stat: 'vida', delta: 20 },
      { kind: 'stat', stat: 'mana', delta: 10 },
    ],
    opciones: [
      {
        id: 'cap2_ir_pipo',
        texto: 'Acercarte a Pipo. Ver qué está haciendo.',
        destino: 'cap2_pipo_mañana',
        intencion: 'empatico',
        condiciones: [{ kind: 'flag', flagId: 'pipo_rescatado' }],
        alFallarCondicion: 'ocultar',
      },
      {
        id: 'cap2_ir_nayade',
        texto: 'Buscar a Náyade. Los asuntos pendientes no esperan.',
        destino: 'cap2_nayade_agenda',
        intencion: 'neutral',
      },
      {
        id: 'cap2_caminar_villa',
        texto: 'Caminar la villa sin rumbo. Escuchar lo que la gente no te cuenta directamente.',
        destino: 'cap2_paseo_villa',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
    ],
  },

  {
    id: 'cap2_pipo_mañana',
    capitulo: 2,
    background: { type: 'village', hint: 'pileta ceremonial chica, piedras en espiral' },
    texto: [
      'Pipo está arreglando piedras. No chicas: medianas, del tamaño de un puño cerrado. Las acomoda en una espiral de dos vueltas y media. La disposición tiene una lógica geométrica que vos no le enseñaste. La disposición tiene, de hecho, una lógica que vos no conocés.',
      'Cuando te ve, deja de acomodarlas. Te mira con una cara nueva: la cara que vos ponés cuando te descubren haciendo algo que todavía no sabés explicar. Si un carpincho pudiera avergonzarse, éste se acaba de avergonzar.',
      'Le acariciás la cabeza. Ronronea —si es que se le puede llamar ronroneo a esa vibración ronca y grave—. Pero en el ronroneo hay una pausa nueva, un corte ínfimo, que vos registrás sin querer. [shake]Algo cambió en él en estos tres días.[/shake] No pensás más en eso todavía. No querés pensar más en eso todavía.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'vio_pipo_acomodar_piedras', valor: true }],
    opciones: [
      {
        id: 'cap2_volver_desde_pipo',
        texto: 'Alzarlo. Llevártelo en brazos. Hay que buscar a Náyade.',
        destino: 'cap2_nayade_agenda',
        intencion: 'empatico',
        efectos: [{ kind: 'stat', stat: 'vinculo', delta: 1 }],
      },
    ],
  },

  {
    id: 'cap2_paseo_villa',
    capitulo: 2,
    background: { type: 'village', hint: 'mercado pequeño entre chozas, conversaciones bajas' },
    texto: [
      'Caminar una aldea que no es la tuya para aprenderla: un oficio viejo. Lo aprendiste cuando eras soldado; lo usaste en los pueblos de la sierra; lo aplicás ahora, con el beneficio suplementario de que acá nadie espera que lo apliques.',
      'Tres retazos te quedan:',
      'Una carpincha joven, bella como una semilla abierta, le dice a su compañera: —Otra familia se fue anoche al sur. La tercera esta semana. [emph]No puede ser lluvia todos los días[/emph]. La otra la manda a callar con una mirada.',
      'Dos viejos junto al fogón central: —El que trajo Ibra la última vez no era elixir, era agua saborizada. Ibra es Ibra. —Ibra siempre fue así. —Pero esta vez cobra cada vez más caro. Y cobra pidiendo [glow color=ember]cosas que no sirven para comerciar[/glow].',
      'Un carpincho que se ríe solo en una esquina. Cuando pasás al lado, deja de reírse. Te mira con ojos que no parpadean. Cuando te alejás, vuelve a reírse. [whisper]No sabés por qué, pero te acordás de él toda la mañana.[/whisper]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'escuchó_rumores_cap2', valor: true },
      { kind: 'xp', cantidad: 20 },
    ],
    opciones: [
      {
        id: 'cap2_desde_paseo_a_nayade',
        texto: 'Ya con la cabeza llena, ir a lo de Náyade.',
        destino: 'cap2_nayade_agenda',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap2_nayade_agenda',
    capitulo: 2,
    titulo: 'Tres puertas y una obligación',
    background: { type: 'village', hint: 'choza central de Náyade, luz filtrada' },
    texto: [
      'Náyade está sola. Tiene sobre las rodillas una tela de lino en la que está bordando algo que a primera vista parece un mapa y a segunda vista parece un árbol genealógico y a tercera vista quizá no sea ninguna de las dos cosas.',
      '—Te esperaba —dice, sin mirarte—. Sentate. Hoy no tengo sopa. Te pedí té. El té está siendo preparado con la demora de rigor.',
      'Vos te sentás. Ella deja el bordado.',
      '—Tres asuntos. Uno acá, en el río: los carpinchos se están enfermando. Algunos mueren. La mayoría se van al sur —a buscar agua, dicen—, y no vuelven. Quiero saber por qué. Dos: Orón te convoca. Tiene una redada planeada sobre un puesto chico de La Sombra. Si vas con él, los Guardianes del Fuego te van a empezar a tomar en serio. Si no vas, no te lo va a reprochar en voz alta pero te lo va a reprochar en voz baja por el resto de tu vida. Tres: los Susurradores, en los Acantilados, te esperan. Vega —la vieja— quiere verte. Cuando Vega quiere ver a alguien no es por aburrimiento. Te va a enseñar algo.',
      '—Hacé los tres en el orden que te parezca. Los tres son necesarios. Dos que hagas no alcanzan.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'cap2_mision_asignada', valor: true }],
    opciones: [
      {
        id: 'cap2_preguntar_sena',
        texto: '[emph]"¿Y Sena?"[/emph] No sabés bien por qué preguntás por ella.',
        destino: 'cap2_nayade_sobre_sena',
        intencion: 'empatico',
      },
      {
        id: 'cap2_saltar_hub',
        texto: 'Aceptar sin pedir más detalles. El tiempo ya se estaba acortando antes de que llegaras.',
        destino: 'cap2_hub',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap2_nayade_sobre_sena',
    capitulo: 2,
    background: { type: 'village', hint: 'Náyade con una sonrisa menor' },
    texto: [
      '—Sena está bien —dice Náyade, con esa precisión cuidadosa que tienen los viejos cuando las respuestas cortas esconden respuestas largas—. Está afuera. Cada cierto tiempo necesita irse. No me preguntes a dónde porque no me lo dice. A los carpinchos jóvenes hay que dejarles las escapadas como se les deja a los chicos humanos: es el único modo de que sepan a qué lugar volver.',
      'Te mira fijo.',
      '—Pero vos no preguntaste por Sena porque sí, Franco. Vos preguntaste porque ella te gustó. No te voy a decir nada. Me limito a constatarlo. A los viejos nos queda eso: constatar.',
      'Te sonrojás. Hace años que no te sonrojás. Odiás un poco a Náyade en ese momento. Náyade, con todo el derecho, se ríe.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'nayade_notó_interes_sena', valor: true }],
    opciones: [
      {
        id: 'cap2_salir_a_hub',
        texto: 'Salir antes de que te lea otra cosa más.',
        destino: 'cap2_hub',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  HUB
  // ===========================================================================
  {
    id: 'cap2_hub',
    capitulo: 2,
    titulo: 'Qué hacer primero',
    background: { type: 'village', hint: 'villa de día, afuera de la choza de Náyade' },
    texto: [
      'Salís al sol flojo. El río murmura como siempre. [if flag=cap2_clanrio_hecho]Náyade te dejó una mirada inquieta cuando le trajiste lo del río. Faltan las otras dos.[/if][if flag=cap2_guardianes_hecho]La redada con Orón ya pasó. Todavía te duelen cosas de esa salida. Faltan las otras dos.[/if][if flag=cap2_susurradores_hecho]El viaje a los Acantilados te dejó algo adentro que todavía no entendés del todo. Faltan las otras dos.[/if]',
      'Decidí por dónde seguís.',
    ],
    opciones: [
      {
        id: 'cap2_ir_rio',
        texto: 'Investigar el río. Los enfermos primero.',
        destino: 'cap2_rio_entrada',
        intencion: 'empatico',
        condiciones: [{ kind: 'flag', flagId: 'cap2_clanrio_hecho', valor: false }],
        alFallarCondicion: 'ocultar',
      },
      {
        id: 'cap2_ir_guardianes',
        texto: 'Ir al puesto de Orón. Hay una redada que hacer.',
        destino: 'cap2_guardianes_oron',
        intencion: 'valiente',
        condiciones: [{ kind: 'flag', flagId: 'cap2_guardianes_hecho', valor: false }],
        alFallarCondicion: 'ocultar',
      },
      {
        id: 'cap2_ir_susurradores',
        texto: 'Viajar a los Acantilados Olvidados. Vega espera.',
        destino: 'cap2_acantilados_viaje',
        intencion: 'astuto',
        condiciones: [{ kind: 'flag', flagId: 'cap2_susurradores_hecho', valor: false }],
        alFallarCondicion: 'ocultar',
      },
      {
        id: 'cap2_avanzar_climax',
        texto: 'Las tres misiones están hechas. Ir a Náyade para cerrar el capítulo.',
        destino: 'cap2_hub_completo',
        intencion: 'neutral',
        condiciones: [
          { kind: 'flag', flagId: 'cap2_clanrio_hecho' },
          { kind: 'flag', flagId: 'cap2_guardianes_hecho' },
          { kind: 'flag', flagId: 'cap2_susurradores_hecho' },
        ],
        alFallarCondicion: 'ocultar',
      },
    ],
  },

  // ===========================================================================
  //  RAMA A — Clan del Río / La plaga
  // ===========================================================================
  {
    id: 'cap2_rio_entrada',
    capitulo: 2,
    titulo: 'El río enfermo',
    background: { type: 'river', hint: 'orilla del río ancestral, carpinchos tristes sentados' },
    texto: [
      'El río, visto de día, tiene un color verde viejo, el verde del agua que estuvo quieta en una olla de cobre: un verde que anuncia moho. Vos sabés que eso no es natural. Lo sabés de tu infancia en el norte, cuando los ríos empezaban a ponerse verdes justo antes de los veranos que se llevaban a las abuelas.',
      'Tres familias de carpinchos están sentadas en la orilla. No hacen nada: no pescan, no juegan, no se lavan. Están sentados mirando el agua con la mansedumbre triste del animal que siente algo mal pero no sabe qué ni contra quién.',
      'En el medio de una choza chica, apartada de las otras, un carpincho de cuatro años arregla una red de pesca con los ojos muy rojos. No es llanto: es fiebre. Se llama Pecora. La hermana mayor, Rami, te recibe en la puerta con una sonrisa que no te cuadra del todo.',
    ],
    opciones: [
      {
        id: 'cap2_entrar_pecora',
        texto: 'Entrar. Ver a Pecora.',
        destino: 'cap2_rio_pecora',
        intencion: 'empatico',
      },
      {
        id: 'cap2_hablar_rami',
        texto: 'Hablar primero con Rami. Hace rato que te intriga cómo mira las cosas.',
        destino: 'cap2_rio_rami_hablar',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap2_rio_rami_hablar',
    capitulo: 2,
    background: { type: 'river', hint: 'Rami en el umbral, atado de la red en la mano' },
    texto: [
      'Rami es mayor de lo que aparenta. Los carpinchos de esta familia tienen ese rasgo: se conservan infantiles hasta pasados los treinta. En la voz, sin embargo, hay algo mayor. Una nota un cuarto de tono por debajo de lo que las demás voces ofrecen.',
      '—Mi hermana está así desde hace dos semanas —dice—. Empezó con fiebre de dos días. Después se le fue. Después volvió, peor. Ayer casi le saca la ropa a mi madre de un manotazo porque creyó que le quería quemar la piel. No. Queremos que la revises. Queremos que la huelas.',
      '—¿Que la huela?',
      '—Sí. Vos sos humano. Vos oliste cosas que nosotros no olemos. Hay un olor raro en ella. Queremos saber si es un olor que vos conocés.',
      'Te mira. En la mirada hay ruego, sí, pero también hay algo más que no te gusta del todo. La certeza anticipada del que ya sabe la respuesta y te pide que la verifiques para poder seguir conviviendo con ella.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'rami_pidió_oler', valor: true }],
    opciones: [
      {
        id: 'cap2_entrar_con_rami',
        texto: 'Entrar con Rami a ver a Pecora.',
        destino: 'cap2_rio_pecora',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap2_rio_pecora',
    capitulo: 2,
    titulo: 'Pecora',
    background: { type: 'village', hint: 'interior de choza, Pecora acostada sobre esteras' },
    texto: [
      'La choza huele a enferma. No es olor sucio: es ese olor específico del cuerpo que está gastando más energía de la que recibe. Dulzón, amargo, sin ruido.',
      'Pecora está echada sobre tres esteras. Tiene los ojos cerrados pero las pestañas le tiemblan como si detrás hubiera actividad eléctrica. La madre —una carpincha gastada, con manos que se mueven demasiado— le cambia el paño de la frente cada treinta segundos, un gesto que vos reconocés: cuando no podés curar, tocás. Tocás y tocás hasta que se te gasta lo humano.',
      'Te acercás. Olés. [if skill=oido_bosque]Apenas ponés la nariz a diez centímetros del pelaje, lo identificás. Hay un olor debajo del olor a enferma: un perfume metálico, casi dulce, como a moho de fierro. No es enfermedad: es [glow color=ember]sustancia[/glow]. Alguien le puso algo al cuerpo, o al agua, o a ambos.[/if][if noskill=oido_bosque]Olés. Hay algo raro. No lo podés nombrar. Te suena vagamente, como una palabra que tenías en la punta de la lengua y te olvidaste.[/if]',
      'Le mirás las palmas. Las manos de los enfermos dicen la verdad. Las de Pecora tienen una pátina gris, apenas un tinte, en la base de los dedos. Vos ese tinte lo viste otra vez en tu vida: en la boca de tu tía, quince años atrás, cuando murió envenenada por los trabajos de la mina.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'pecora_examinada', valor: true },
      { kind: 'xp', cantidad: 30 },
    ],
    opciones: [
      {
        id: 'cap2_decir_rami_sospecha',
        texto: 'Decirle a Rami que esto no es enfermedad.',
        destino: 'cap2_rami_reacciona',
        intencion: 'valiente',
      },
      {
        id: 'cap2_no_decir_investigar',
        texto: 'No decir nada. Pedir revisar el agua del pozo familiar.',
        destino: 'cap2_rio_investigar_agua',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
    ],
  },

  {
    id: 'cap2_rami_reacciona',
    capitulo: 2,
    background: { type: 'village', hint: 'Rami con cara de neutralidad forzada' },
    texto: [
      'Lo decís sin mirarlo: "Esto no es enfermedad. Esto es veneno."',
      'Rami no se mueve un milímetro. La madre sí: se le cae el paño de la mano. Rami entonces baja la cabeza y asiente con una lentitud estudiada, como si estuviera agradeciendo una confirmación que llevaba tiempo esperando.',
      '—Sabía —dice, muy bajito—. Sabía, pero no quería saber. Ella va a morir, ¿no?',
      'Vos no respondés inmediatamente. La pregunta no es para vos. Es para él mismo. Te das cuenta entonces de algo: Rami no está sorprendido. Rami está [glow color=ember]confirmando[/glow]. Vos no sabés qué está confirmando todavía. Pero lo vas a saber.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'rami_sospechoso', valor: true }],
    opciones: [
      {
        id: 'cap2_ir_a_pozo',
        texto: 'Pedirle a Rami que te lleve al pozo del agua.',
        destino: 'cap2_rio_investigar_agua',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap2_rio_investigar_agua',
    capitulo: 2,
    background: { type: 'river', hint: 'pozo pequeño cubierto con tapa de madera, junto a la orilla' },
    texto: [
      'El pozo familiar está a cincuenta pasos de la choza. Es una excavación chica, tapada con una madera circular que tiene las marcas del agua en los bordes. Al lado, dos palas humanas —de donde vinieron esas palas es una pregunta separada— apoyadas contra la empalizada.',
      'Rami destapa. El agua está calma. Vos te arrodillás. Olés. Sumergís un dedo y lo sacás: limpio. No hay nada visible.',
      '[if skill=oido_bosque]Cerrás los ojos. Escuchás. El agua buena tiene un sonido que uno no registra porque es lo normal: un zumbido leve, vivo. Ésta no zumba. Está muerta.[/if][if spell=vinculo_empatico]Tocás el agua y algo te vuelve: una imagen. Una mano chica —carpincha— volcando un frasquito diminuto al amanecer. La imagen se disuelve rápido. No alcanza para saber de quién es la mano. Pero alcanza para saber que [glow color=mystic]fue a propósito[/glow].[/if][if noskill=oido_bosque]Vos no sabés agua. Vos sabés cabañas. Te arrimás a un rincón donde al parecer se acumula sedimento, frotás el dedo contra la piedra, lo sacás. Un tinte fino, casi imperceptible, gris. El mismo gris que te encontraste en las manos de Pecora.[/if]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'agua_es_envenenada', valor: true },
      { kind: 'xp', cantidad: 30 },
    ],
    opciones: [
      {
        id: 'cap2_confrontar_rami',
        texto: 'Darte vuelta. Mirar a Rami.',
        destino: 'cap2_confrontar_rami',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap2_confrontar_rami',
    capitulo: 2,
    titulo: 'Rami',
    background: { type: 'river', hint: 'Rami arrodillado, frasco en la mano, mirada caída' },
    texto: [
      'Te das vuelta. No hace falta acusarlo. Él ya está arrodillado. Tiene un frasquito muy chiquito —pasaría por frasco de tinta— apretado entre los dedos. La tapa está floja. La mano le tiembla.',
      '—Empecé hace dos meses —dice, y la voz le sale rota—. No es por mí. Los de La Sombra tienen a mi hermano mayor. Lo secuestraron hace un año. Me dijeron que si vaciaba dos gotas por día del frasco, al final me lo devolvían. Me prometieron que nadie moría, que se empezaban a sentir mal y se iban al sur, que es lo que ellos querían. Me mintieron. Mi hermana se está muriendo. Por mi mano.',
      '—No les dije nada a los otros. Ni a Náyade. No quería creerlo. Cada vez que ponía las gotas me decía que esa era la última. Cada vez mentí.',
      'Mira el frasco. Lo rompe con un golpe chico contra la tapa del pozo. El frasco estalla. El contenido se derrama sobre la piedra y empieza a humear apenas: lo que sea que contiene es caliente para la superficie.',
      '—Me entrego —dice—. Que decida Náyade.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'rami_confesó', valor: true },
      { kind: 'xp', cantidad: 50 },
    ],
    opciones: [
      {
        id: 'cap2_rami_perdon',
        texto: '[emph]"Te llevamos con Náyade. Ella decide. Yo te voy a pedir que decida suave."[/emph]',
        destino: 'cap2_rio_cierre',
        intencion: 'empatico',
        efectos: [
          { kind: 'flag', flagId: 'rami_perdonado', valor: true },
          { kind: 'stat', stat: 'empatia', delta: 1 },
          { kind: 'reputation', facciónId: 'clan_rio', delta: 15 },
        ],
      },
      {
        id: 'cap2_rami_dureza',
        texto: '[emph]"Vas a confesar todo a Orón. Él decide."[/emph] Orón no decide suave.',
        destino: 'cap2_rio_cierre',
        intencion: 'hostil',
        efectos: [
          { kind: 'flag', flagId: 'rami_entregado_oron', valor: true },
          { kind: 'reputation', facciónId: 'guardianes_fuego', delta: 15 },
        ],
      },
      {
        id: 'cap2_rami_ayudar',
        texto: '[emph]"Vamos a buscar a tu hermano. Sin matarte, sin entregarte. Los dos. Los dos vivos."[/emph]',
        destino: 'cap2_rio_cierre',
        intencion: 'empatico',
        condiciones: [{ kind: 'stat', stat: 'empatia', minimo: 3 }],
        alFallarCondicion: 'atenuar',
        efectos: [
          { kind: 'flag', flagId: 'rami_pacto_rescate', valor: true },
          { kind: 'stat', stat: 'empatia', delta: 1 },
          { kind: 'xp', cantidad: 40 },
        ],
      },
    ],
  },

  {
    id: 'cap2_rio_cierre',
    capitulo: 2,
    background: { type: 'village', hint: 'vuelta a la villa, luz ocaso' },
    texto: [
      'Volvés a la villa con Rami. [if flag=rami_perdonado]Le das la mano, cosa rara entre humanos y carpinchos pero que él acepta.[/if][if flag=rami_entregado_oron]Lo atás vos mismo, con una soga corta.[/if][if flag=rami_pacto_rescate]Él camina a tu lado. Sin soga. Ustedes dos tienen ahora un pacto que los va a atar más que cualquier soga.[/if]',
      'Ibra, que siempre aparece cuando algo interesante termina, te mira pasar desde su puesto y levanta una ceja tan cargada de preguntas que te molesta la ceja.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cap2_clanrio_hecho', valor: true },
      { kind: 'reputation', facciónId: 'clan_rio', delta: 10 },
      { kind: 'xp', cantidad: 50 },
    ],
    opciones: [
      {
        id: 'cap2_volver_al_hub_desde_rio',
        texto: 'Volver al hub. Faltan cosas.',
        destino: 'cap2_hub',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  RAMA B — Guardianes del Fuego / La redada
  // ===========================================================================
  {
    id: 'cap2_guardianes_oron',
    capitulo: 2,
    titulo: 'Orón',
    background: { type: 'forest', hint: 'puesto de los Guardianes del Fuego, fogón encendido al mediodía' },
    texto: [
      'El puesto de Orón no es una aldea: es una declaración. Una empalizada de troncos afilados, un fogón central que no se apaga nunca, dos postes con cráneos de puma blanqueados por el sol colgando de cuerdas largas. Un lugar diseñado para que uno entienda, a la primera mirada, que quienes habitan acá no tienen tiempo para ceremonias.',
      'Orón te recibe sin levantarse del tronco donde está sentado. Limpia una hoja de pedernal con una franela de tela humana —¿de dónde sacaron esa franela? ¿de la casa de qué ermitaño?—. Te mira dos segundos. Asiente.',
      '—Viniste. Sentate. Hay una cueva a tres horas al oeste. Un puesto chico de La Sombra, guardado por tres, tal vez cuatro. Ahí tienen a alguien nuestro desde hace tres semanas. Vamos a sacarlo. Vos venís. Si te hace sentir útil, usá esa sensación para algo. Si no, igual venís.',
      '—Mi gente no viene. Vengo yo. Venís vos. Así nos conocemos bien. Así uno sabe si el otro sirve o no sirve. Si el otro no sirve, uno lo entiende rápido y sigue su vida.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'conocido_guardianes_fuego', valor: true }],
    opciones: [
      {
        id: 'cap2_aceptar_oron',
        texto: '[emph]"Vamos."[/emph]',
        destino: 'cap2_guardianes_camino',
        intencion: 'valiente',
        efectos: [{ kind: 'reputation', facciónId: 'guardianes_fuego', delta: 10 }],
      },
      {
        id: 'cap2_preguntar_quien_secuestrado',
        texto: '[emph]"¿Quién es el que tienen?"[/emph]',
        destino: 'cap2_oron_responde_secuestrado',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap2_oron_responde_secuestrado',
    capitulo: 2,
    background: { type: 'forest', hint: 'Orón trabajando la hoja, sin mirar a Franco' },
    texto: [
      'Orón no levanta la mirada de la hoja.',
      '—Mi hijo. Karn. Veinte años. Se fue hace tres semanas en patrullaje solitario, a pesar de que yo le dije que no. Dos días después apareció una nota amarrada a la pata de un cuervo —negro, exquisitamente entrenado, mejor comunicación que la nuestra— con una frase corta. "Lo tenemos. Viene cuando venimos." Ni firma. La dejaron ahí para que yo acepte lo que viene.',
      '—Me niego a aceptar lo que viene. Lo saco ahora o lo saco muerto. Ese es el cálculo. Vamos.',
      'Te das cuenta de que Orón nunca te miró a la cara mientras te contaba esto. Un hombre de piedra tiene sus límites como todo el mundo.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'sabe_karn_es_hijo_oron', valor: true },
      { kind: 'stat', stat: 'empatia', delta: 1 },
    ],
    opciones: [
      {
        id: 'cap2_ir_con_oron_post_saber',
        texto: 'Asentir. No decir lo que se te cruzó por la cabeza.',
        destino: 'cap2_guardianes_camino',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap2_guardianes_camino',
    capitulo: 2,
    background: { type: 'forest', hint: 'bosque denso del oeste, luz en gotas por las hojas' },
    texto: [
      'Tres horas con Orón. Tres horas en las que él no habla y vos te acostumbrás a no esperar que hable. El silencio de un guerrero viejo es didáctico: uno aprende a caminar sin pisar hojas secas, a respirar con la mitad del aire, a escanear los árboles a media altura en lugar de al nivel del suelo.',
      'A la segunda hora, en un claro chico, Orón se para. No hace falta señal. Levanta dos dedos. Agachada detrás de un arbusto, una figura inmóvil que ustedes no habían visto. Un vigía de La Sombra.',
      'Orón te mira. Un parpadeo. Te deja la decisión a vos.',
    ],
    opciones: [
      {
        id: 'cap2_vigia_silencio',
        texto: 'Sigilo. Bordear. No gastar este enfrentamiento si se puede evitar.',
        destino: 'cap2_puesto_sombra',
        intencion: 'astuto',
        condiciones: [{ kind: 'stat', stat: 'astucia', minimo: 3 }],
        alFallarCondicion: 'atenuar',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
      {
        id: 'cap2_vigia_combate',
        texto: 'Acabarlo ahora. Uno menos en la cueva.',
        destino: 'cap2_combate_vigia',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap2_combate_vigia',
    capitulo: 2,
    titulo: 'Vigía de La Sombra',
    background: { type: 'forest', hint: 'pelea en claro bajo, hojas volando' },
    texto: [
      'El vigía los vio un segundo antes de que los vieran. Reflejos de sombra: están bien entrenados.',
    ],
    combate: {
      enemyId: 'capibara_sombra',
      onVictoria: 'cap2_puesto_sombra',
      onDerrota: 'cap2_guardianes_fracaso',
      onHuida: 'cap2_guardianes_fracaso',
    },
    opciones: [],
  },

  {
    id: 'cap2_puesto_sombra',
    capitulo: 2,
    titulo: 'La cueva',
    background: { type: 'cave', hint: 'entrada de cueva con braseros apagados al costado' },
    texto: [
      'La entrada de la cueva está a veinte pasos de donde cayó el vigía (o donde ustedes pasaron por al lado sin que los viera). Adentro se escuchan voces: dos, tal vez tres. Una risa chica. Una conversación trivial, el tipo de conversación que se tiene cuando uno lleva días en un puesto sin nada que hacer.',
      'Orón te susurra, por primera vez: —Entro yo. Vos tapás la salida. Si sale alguien corriendo, lo trabás. No te pido que lo mates: te pido que lo trabés. Yo me ocupo.',
      'Entra. Vos esperás al costado de la entrada, daga en mano. A los veinte segundos escuchás un grito. A los cuarenta, un golpe seco. A los sesenta, silencio.',
      'A los setenta, sale corriendo un carpincho chico —bien chico, casi una cría— con los ojos blancos de miedo. Te lo topás.',
    ],
    opciones: [
      {
        id: 'cap2_dejar_escapar_cria',
        texto: 'Dejarlo pasar. Es una cría. No estás ahí para eso.',
        destino: 'cap2_rescate_karn',
        intencion: 'empatico',
        efectos: [
          { kind: 'stat', stat: 'empatia', delta: 1 },
          { kind: 'flag', flagId: 'cap2_liberó_cria_sombra', valor: true },
          { kind: 'reputation', facciónId: 'guardianes_fuego', delta: -5 },
        ],
      },
      {
        id: 'cap2_trabar_cria',
        texto: 'Trabarla. Orón te pidió eso. No le fallás a Orón.',
        destino: 'cap2_rescate_karn',
        intencion: 'neutral',
        efectos: [
          { kind: 'flag', flagId: 'cap2_entregó_cria_sombra', valor: true },
          { kind: 'reputation', facciónId: 'guardianes_fuego', delta: 10 },
        ],
      },
      {
        id: 'cap2_matar_cria',
        texto: 'Cortarla. Es enemiga. Las crías también crecen.',
        destino: 'cap2_rescate_karn',
        intencion: 'hostil',
        efectos: [
          { kind: 'flag', flagId: 'cap2_mató_cria_sombra', valor: true },
          { kind: 'stat', stat: 'empatia', delta: -2 },
          { kind: 'reputation', facciónId: 'sombra', delta: -20 },
          { kind: 'reputation', facciónId: 'clan_rio', delta: -10 },
        ],
      },
    ],
  },

  {
    id: 'cap2_rescate_karn',
    capitulo: 2,
    background: { type: 'cave', hint: 'interior de cueva, Orón ayudando a alguien a levantarse' },
    texto: [
      'Entrás. Orón está acuclillado al lado de un carpincho joven, flaco, encadenado al muro por una pata trasera con una cadena que vos conocés: cadena humana, de herrero. Karn.',
      'Está vivo. Está mal, pero vivo. Tiene la piel irritada, los ojos turbios, la mirada perdida y lenta. Pero reconoce a su padre. Alza una mano, apenas, para tocarle el hocico. Orón no contesta al gesto: le suelta la cadena. Lo alza. La fortaleza física del viejo te deja un momento pensando en cuántas cosas no sabés de él.',
      '[if flag=cap2_mató_cria_sombra]Cuando saliste, quedó una mancha oscura en la entrada. Orón la mira de pasada. No comenta. Te mira a vos. No sabés qué lee en tu cara. No sabés qué leés en la de él.[/if]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'karn_rescatado', valor: true },
      { kind: 'xp', cantidad: 60 },
    ],
    opciones: [
      {
        id: 'cap2_volver_con_karn',
        texto: 'Volver al puesto de Orón con Karn.',
        destino: 'cap2_guardianes_retorno',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap2_guardianes_fracaso',
    capitulo: 2,
    background: { type: 'forest', hint: 'retirada herida entre árboles' },
    texto: [
      'Cae el vigía. Los adentran con ustedes en el aire. Orón pelea como un hombre que ya no espera vivir, y esta vez a su lado vos no fuiste suficiente. Karn queda. Orón sale con sangre en el lomo y el silencio más largo de su vida.',
      'En el camino de vuelta no te dice nada. Eso es peor que si te hubiera culpado.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'karn_no_rescatado', valor: true },
      { kind: 'reputation', facciónId: 'guardianes_fuego', delta: -15 },
      { kind: 'scheduleConsequence', consequenceId: 'trigger:cap3_intro:karn_perdido' },
    ],
    opciones: [
      {
        id: 'cap2_volver_derrotado',
        texto: 'Volver con Orón.',
        destino: 'cap2_guardianes_retorno',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap2_guardianes_retorno',
    capitulo: 2,
    background: { type: 'forest', hint: 'vuelta al puesto de Orón, sol bajando' },
    texto: [
      '[if flag=karn_rescatado]En el puesto, Orón apoya a Karn en una camilla improvisada de juncos. Le dice al oído algo que vos no alcanzás a oír. Karn asiente y cierra los ojos. Orón se para. Te mira. Te da la mano, por primera y última vez en esta historia, con la fuerza innecesaria del que no sabe cómo agradecer.[/if][if flag=karn_no_rescatado]El puesto te recibe con el silencio del que ya entendió, sin que nadie lo diga. Orón se sienta junto al fogón y no se levanta hasta la madrugada. Vos te quedás cerca, sin atreverte a irte. A la madrugada te dice, sin mirarte: —Andá. Tenés otras obligaciones. No tenés ninguna conmigo. No llegaste a tiempo. No es culpa tuya. Andá.[/if]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cap2_guardianes_hecho', valor: true },
      { kind: 'xp', cantidad: 40 },
    ],
    opciones: [
      {
        id: 'cap2_vuelta_a_hub_desde_guardianes',
        texto: 'Volver a la villa. Hay más cosas que hacer.',
        destino: 'cap2_hub',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  RAMA C — Susurradores / El hechizo
  // ===========================================================================
  {
    id: 'cap2_acantilados_viaje',
    capitulo: 2,
    titulo: 'Los Acantilados Olvidados',
    background: { type: 'cliffs', hint: 'acantilados al atardecer, viento cortante' },
    texto: [
      'El camino a los Acantilados es un camino que no se hace con prisa. Ocho horas. Trepás por una piedra que te reclama rodilla, bajás por una grieta que te cobra codo, cruzás un río en el punto donde, te dijeron, hay que cruzarlo y no en cualquier otro.',
      'Llegás al tope cuando el sol está cayendo. Los Acantilados Olvidados son una herradura de roca roja que da a un mar que no parece mar —¿puede haber mar acá?, te preguntás, y no alcanzás a formular la pregunta entera porque el viento se te la lleva—. Hay casas excavadas en la roca. Humo saliendo de tres. Nada más.',
      'Te esperan. Cinco carpinchos parados en semicírculo al borde de la meseta. No vinieron al verte: estaban ya ahí. Probablemente desde la mañana, probablemente desde hace dos días.',
      'En el centro del semicírculo, una carpincha vieja con los ojos blancos, ciegos con la claridad con la que los ciegos ven a los videntes. Vega.',
    ],
    onEnter: [
      { kind: 'unlockLocation', locacionId: 'acantilados_olvidados' },
      { kind: 'flag', flagId: 'conocido_susurradores', valor: true },
      { kind: 'xp', cantidad: 30 },
    ],
    opciones: [
      {
        id: 'cap2_acercarse_vega',
        texto: 'Acercarte a Vega.',
        destino: 'cap2_vega_recepcion',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap2_vega_recepcion',
    capitulo: 2,
    titulo: 'Vega',
    background: { type: 'mystical', hint: 'círculo de piedras, Vega sentada sobre un trono de junco' },
    texto: [
      'Vega se sienta. Los otros se disuelven como por instrucción telepática; dos se quedan, tres desaparecen entre las grietas de la roca. La disolución es elegante: no hay corte, no hay despedida, simplemente hay.',
      '—Franco —dice Vega, sin mirarte (no puede, no va a poder; pero la palabra "mirar" se le aplica a ella de otra manera)—. Llegaste tarde para lo que tenía pensado decirte pronto, y temprano para lo que pensaba decirte tarde. Es el problema de los ciegos: sabemos los tiempos pero no sabemos los relojes.',
      '—Te voy a contar tres cosas. Y después te voy a dar un regalo. Y después te voy a hacer una pregunta. Si sobrevivís a la pregunta, volvés con lo que te doy. Si no, no volvés. Vas a sobrevivir. Lo sé, pero no te lo digo para tranquilizarte: te lo digo porque el saberlo es parte de sobrevivir.',
    ],
    opciones: [
      {
        id: 'cap2_vega_escuchar',
        texto: 'Sentarte. Escuchar.',
        destino: 'cap2_vega_tres_cosas',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap2_vega_tres_cosas',
    capitulo: 2,
    background: { type: 'mystical', hint: 'Vega con las manos abiertas hacia el cielo' },
    texto: [
      '—Primera cosa: Pipo no es lo que ustedes creen. [if flag=pipo_rescatado]El pibe que tenés en brazos no es solo un huérfano con sangre de Guardián. Es un recipiente. Alguien pensado para sostener algo que todavía no llegó. Lo que está por venir no cabe en un carpincho solo: necesita un carpincho [emph]preparado[/emph]. Pipo fue preparado antes de que ustedes lo criaran.[/if][if noflag=pipo_rescatado]El pibe que dejaste que se llevaran no es solo un huérfano. Es un recipiente. Alguien va a echarle algo encima que todavía no terminó de llegar. Quien lo tenga cuando llegue, decide.[/if]',
      '—Segunda cosa: los incendios no son destrucción. Los incendios son [glow color=ember]señales[/glow]. Alguien que vive muy lejos de acá los está usando para encontrar algo. No te voy a decir qué, porque si te lo digo ahora me parás y no me escuchás el resto. Pero es menos ambicioso de lo que vas a pensar y más grave al mismo tiempo.',
      '—Tercera: hay un traidor entre los tuyos. En tu villa. Alguien que ustedes quieren. Yo no veo nombres: veo sombras. La sombra de ese traidor tiene el tamaño de una persona joven. Eso es todo lo que puedo decir.',
      'Te deja un minuto de silencio. Es un minuto largo, el más largo de tu vida hasta ahora.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'vega_reveló_tres_cosas', valor: true },
      { kind: 'xp', cantidad: 50 },
    ],
    opciones: [
      {
        id: 'cap2_vega_regalo',
        texto: '[emph]"El regalo."[/emph] Necesitás algo para tragar todo esto.',
        destino: 'cap2_vega_hechizo',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap2_vega_hechizo',
    capitulo: 2,
    titulo: 'Vínculo Empático',
    background: { type: 'mystical', hint: 'Vega con la mano a dos centímetros de la frente de Franco' },
    texto: [
      'Vega no te toca. Te acerca la palma a dos centímetros de la frente, y algo en ese espacio diminuto se cierra como un puño. Cuando lo abre, lo abre adentro tuyo. Un lugar nuevo. No es una herida. Es un hueco, listo para que algo se meta ahí y se acomode.',
      '—Desde hoy —dice— cuando toques a alguien —o algo que retuvo memoria— vas a poder ver un fragmento. Uno. Elegido por vos, elegido por la cosa: no siempre vas a decidir cuál. Los Susurradores usamos esto para mucho. Vos lo vas a usar, imagino, para una cosa puntual, que ya empezaste a necesitar sin saberlo. Usala con sobriedad: cada Vínculo Empático te cuesta un pedacito de pureza. A los cinco años de usarlo mucho, uno empieza a ver cosas sin tocar. Eso no es un don: es el comienzo del fin.',
      'Cerrás los ojos. Cuando los abrís, estás en el mismo sitio pero [glow color=mystic]distinto[/glow].',
    ],
    onEnter: [
      { kind: 'unlockSpell', hechizoId: 'vinculo_empatico' },
      { kind: 'reputation', facciónId: 'susurradores', delta: 30 },
      { kind: 'xp', cantidad: 80 },
    ],
    opciones: [
      {
        id: 'cap2_vega_pregunta',
        texto: 'Esperar la pregunta.',
        destino: 'cap2_vega_pregunta_final',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap2_vega_pregunta_final',
    capitulo: 2,
    background: { type: 'mystical', hint: 'Vega con cabeza ligeramente inclinada' },
    texto: [
      'Vega respira. Se toca el pecho con la palma.',
      '—¿Qué hiciste con tu madre, Franco?',
      'Te quedás en blanco. No por la pregunta en sí: por lo impensable de que alguien, acá, ahora, te esté preguntando por tu madre. La respuesta breve es que la dejaste morir sola, siete años atrás, cuando te volviste ermitaño. La respuesta larga incluye una carta que no le contestaste, una deuda que no pagaste, una palabra dicha en un momento malo.',
      'No te pide la respuesta larga. No te pide la corta. Te pide la pregunta. La pregunta se queda flotando entre ustedes durante un tiempo que podría ser un minuto o una hora.',
      '—Volvé a tu villa —dice, al fin—. Te hacía falta sostener eso sin contestar. El que no contesta nada, en el momento justo, se acuerda después. Acordate.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'vega_preguntó_madre', valor: true },
      { kind: 'flag', flagId: 'cap2_susurradores_hecho', valor: true },
      { kind: 'xp', cantidad: 60 },
    ],
    opciones: [
      {
        id: 'cap2_bajar_acantilados',
        texto: 'Bajar los acantilados. Volver.',
        destino: 'cap2_hub',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  CLÍMAX
  // ===========================================================================
  {
    id: 'cap2_hub_completo',
    capitulo: 2,
    titulo: 'La noche de la costura',
    background: { type: 'village', hint: 'villa al anochecer, fogones bajos' },
    texto: [
      'Volvés con tres misiones cumplidas —o dos y media, según cómo lleves la cuenta— y una cabeza que te pesa como si la hubieras estado cargando por alguien más. Náyade te recibe en la puerta de su choza. Te estudia un segundo de más.',
      '—Algo cambió en vos —dice—. Tres cosas al mismo tiempo. Bien. Vamos adentro. Te tengo una última cosa que pedirte esta noche.',
      'Adentro, sobre la mesa de piedra, hay una estera enrollada. La desenrolla. Hay un cuerpo encima. Pecora.',
      '—Se está yendo —dice Náyade—. Lleva dos horas sin abrir los ojos. No alcanzamos a salvarla con las hierbas del río. Pero puede que lo que tiene adentro sirva a los demás. Si vos te animás a entrar.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'pecora_moribunda', valor: true }],
    opciones: [
      {
        id: 'cap2_usar_vinculo',
        texto: 'Apoyar la mano en la frente de Pecora. Usar Vínculo Empático.',
        destino: 'cap2_visión_pecora',
        intencion: 'empatico',
        condiciones: [{ kind: 'spell', hechizoId: 'vinculo_empatico' }],
        alFallarCondicion: 'atenuar',
      },
      {
        id: 'cap2_preguntar_sin_spell',
        texto: '[emph]"¿Y si no me animo?"[/emph]',
        destino: 'cap2_nayade_sin_spell',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap2_nayade_sin_spell',
    capitulo: 2,
    background: { type: 'village', hint: 'Náyade seria frente a Pecora' },
    texto: [
      '—Si no te animás, ella se va a llevar a la tumba lo que sabe. Y lo que sabe puede salvar al resto. Vega no te dio el Vínculo Empático por coincidencia. Te lo dio porque esta noche iba a hacer falta. Andá adentro. Esto te va a doler. Pero si ves algo útil, una aldea entera te lo va a agradecer.',
    ],
    opciones: [
      {
        id: 'cap2_entrar_al_spell',
        texto: 'Apoyar la mano.',
        destino: 'cap2_visión_pecora',
        intencion: 'empatico',
        condiciones: [{ kind: 'spell', hechizoId: 'vinculo_empatico' }],
        alFallarCondicion: 'ocultar',
      },
    ],
  },

  {
    id: 'cap2_visión_pecora',
    capitulo: 2,
    titulo: 'La visión',
    background: { type: 'mystical', hint: 'visión en blanco y negro, imagen fragmentada' },
    texto: [
      'Tocás la frente. El Vínculo se activa con un tirón seco, como si algo dentro tuyo se hubiera enchufado a la corriente de ella. La sala desaparece. No estás.',
      'Estás en un pozo. Visto desde arriba: un pozo con su agua, un cielo gris, una mano chica volcando de un frasco oscuro. La mano tiembla. La mano —la reconocés de golpe— no es la que creíste. No es la de Rami. Es [glow color=ember]la de Sena[/glow]. Sena vertiendo el veneno. Sena haciendo por las noches lo que Rami hacía por las mañanas. Dos saboteadores, no uno. Trabajando sin conocerse.',
      'La imagen se amarra un segundo más. Sena, al fondo del pozo, susurra: [whisper]"Por favor, que no duela mucho. Por favor, que sea rápido."[/whisper] Y se va.',
      'Volvés. Estás en la choza. Náyade te agarra de los hombros porque sino te caías. Pecora ya dejó de respirar.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'visión_reveló_sena', valor: true },
      { kind: 'flag', flagId: 'pecora_muerta', valor: true },
      { kind: 'xp', cantidad: 80 },
    ],
    opciones: [
      {
        id: 'cap2_informar_nayade_sena',
        texto: 'Decírselo a Náyade.',
        destino: 'cap2_nayade_absorbe',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap2_nayade_absorbe',
    capitulo: 2,
    background: { type: 'village', hint: 'Náyade muy quieta' },
    texto: [
      'Náyade no se inmuta. Eso es lo más terrible: la quietud de una líder vieja frente a una noticia que debería quebrarla. Pero vos te das cuenta de que no está rota: está [glow color=mystic]decidiendo[/glow]. Recalibrando. Entendiendo qué política sigue y cuál ya no.',
      '—Sena —dice al fin—. Hija mía. No por sangre. Por nosotras. La tengo desde que tenía tres semanas. Pensar la traición de ella me va a costar más que la muerte. Pensar [emph]por qué[/emph] la hizo me va a costar todavía más.',
      '—No le vamos a decir nada. Todavía. Que siga siendo la que duerme en la hamaca de al lado tuya. Quiero entender por qué. Cuando lo entienda, te aviso. Mientras tanto, Franco, cuidála sin que se entere de que la cuidás. Es lo más difícil que te voy a pedir este mes.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'nayade_sabe_sena', valor: true }],
    opciones: [
      {
        id: 'cap2_salir_a_dormir',
        texto: 'Asentir. Salir. La noche se te hizo mucho más larga.',
        destino: 'cap2_noche_final',
        intencion: 'empatico',
      },
    ],
  },

  // ===========================================================================
  //  CIERRE
  // ===========================================================================
  {
    id: 'cap2_noche_final',
    capitulo: 2,
    titulo: 'La hamaca vacía',
    background: { type: 'village', hint: 'villa de noche, niebla baja sobre las pasarelas' },
    texto: [
      'No dormís. Nadie duerme cuando sabe algo que los demás no saben. Te quedás mirando la hamaca de Sena: desocupada, impecable, como una promesa rota antes de ser hecha.',
      'A las tres de la mañana la niebla se levanta. A las tres y cinco escuchás pasos cortos sobre la pasarela. A las tres y seis los pasos se alejan hacia el norte.',
      'A las cuatro, cuando te levantás por fin, la hamaca sigue desocupada. No es que Sena no volvió. Es que Sena [shake]se fue[/shake]. La manta está doblada con una prolijidad que ella nunca tuvo. Alguien la dobló por ella. Alguien la dobló después.',
      'Afuera, en la barandilla, hay una piedra chica con un grabado en espiral. La misma espiral que Pipo estaba armando con las piedras ceremoniales esa mañana. Alguien la dejó ahí para que la encontraras. Alguien sabe que vos la vas a entender sin que se la expliquen.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'sena_desapareció', valor: true },
      { kind: 'item', itemId: 'piedra_espiral', delta: 1 },
      { kind: 'scheduleConsequence', consequenceId: 'trigger:cap3_intro:sena_se_fue' },
    ],
    opciones: [
      {
        id: 'cap2_despertar_villa',
        texto: 'Despertar a Náyade. Mostrarle la piedra.',
        destino: 'cap2_fin',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap2_fin',
    capitulo: 2,
    titulo: 'Fin del Capítulo 2',
    background: { type: 'village', hint: 'amanecer gris, villa despertándose' },
    texto: [
      'Náyade mira la piedra sin tocarla. Cuando habla, es una voz más gastada que la de ayer. Un día en la vida de un viejo a veces vale diez.',
      '—Se fue con los que tenían a su hermano —dice—. No lo sabía hasta ahora. Que tenía hermano. Tanto no sabíamos de ella.',
      'Te mira. Y por primera vez desde que la conociste, Náyade te pide algo con toda la humildad de los que ya no tienen otra cosa que pedir.',
      '—Traéla de vuelta, Franco. No para castigarla. Para entender. Si la traés, puede que salvemos a muchos. Si la perdemos, la perdemos con lo que sabe. Y lo que sabe va a ser la diferencia entre el capítulo que viene y el después del que viene.',
      '[whisper](Fin del Capítulo 2 — "La red debajo del agua". Seguimos en el Capítulo 3.)[/whisper]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cap2_completado', valor: true },
      { kind: 'xp', cantidad: 120 },
    ],
    opciones: [
      {
        id: 'cap2_entrar_cap3',
        texto: 'Asentir. Entrar al Capítulo 3.',
        destino: 'cap3_intro',
        intencion: 'valiente',
      },
    ],
  },
];
